// src/pages/document/DocumentDashboardPage.tsx
// Auto-generated by corebuilder. Edit freely.

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { observer } from '@react';
import { useDocument, useI18n } from '@react/hooks';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  TableEmpty,
  ErrorAlert,
} from '@react/components';

/**
 * Document dashboard page.
 */
export const DocumentDashboardPage = observer(() => {
  const store = useDocument();
  const { t } = useI18n();
  const [stats, setStats] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const loadStats = async () => {
      setIsLoading(true);
      try {
        const response = await store.GetDocumentStats();
        setStats(response);
      } catch {
        // Error is handled by store
      } finally {
        setIsLoading(false);
      }
    };
    loadStats();
  }, []);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page header — the tab already names this page, so the heading is screen-reader only. */}
      <div>
        <h1 className="sr-only">{t('document.dashboard')}</h1>
        <nav className="text-sm">
          <Link to="/documents" className="text-muted-foreground hover:text-foreground">
            {t('document.list')}
          </Link>
          <span className="mx-2 text-muted-foreground">/</span>
          <span className="text-foreground">{t('document.dashboard')}</span>
        </nav>
      </div>

      {store.error ? <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} /> : null}

      {/* Row 1: Document counts */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-6">
            <p className="text-sm font-medium text-muted-foreground">{t('document.totalDocuments')}</p>
            <p className="text-3xl font-bold text-foreground mt-1">
              {String(stats?.total_documents ?? 0)}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <p className="text-sm font-medium text-muted-foreground">{t('document.activeDocuments')}</p>
            <p className="text-3xl font-bold text-foreground mt-1">
              {String(stats?.active_documents ?? 0)}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <p className="text-sm font-medium text-muted-foreground">{t('document.draftDocuments')}</p>
            <p className="text-3xl font-bold text-foreground mt-1">
              {String(stats?.draft_documents ?? 0)}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <p className="text-sm font-medium text-muted-foreground">{t('document.archivedDocuments')}</p>
            <p className="text-3xl font-bold text-foreground mt-1">
              {String(stats?.archived_documents ?? 0)}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Row 2: Review status */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-6">
            <p className="text-sm font-medium text-muted-foreground">{t('document.pendingReview')}</p>
            <p className="text-3xl font-bold text-foreground mt-1">
              {String(stats?.pending_review ?? 0)}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <p className="text-sm font-medium text-muted-foreground">{t('document.overdueReview')}</p>
            <p className="text-3xl font-bold text-destructive mt-1">
              {String(stats?.overdue_review ?? 0)}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <p className="text-sm font-medium text-muted-foreground">{t('document.upcomingReview30d')}</p>
            <p className="text-3xl font-bold text-foreground mt-1">
              {String(stats?.upcoming_review_30d ?? 0)}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Row 3: Pending items */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-6">
            <p className="text-sm font-medium text-muted-foreground">{t('document.pendingApproval')}</p>
            <p className="text-3xl font-bold text-foreground mt-1">
              {String(stats?.pending_approval ?? 0)}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <p className="text-sm font-medium text-muted-foreground">{t('document.pendingAcknowledgments')}</p>
            <p className="text-3xl font-bold text-foreground mt-1">
              {String(stats?.pending_acknowledgments ?? 0)}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <p className="text-sm font-medium text-muted-foreground">{t('document.overdueAcknowledgments')}</p>
            <p className="text-3xl font-bold text-destructive mt-1">
              {String(stats?.overdue_acknowledgments ?? 0)}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* By Type */}
      <Card>
        <CardHeader>
          <CardTitle>{t('document.byType')}</CardTitle>
          <CardDescription>{t('document.byTypeDescription')}</CardDescription>
        </CardHeader>
        <CardContent>
          <Table striped hoverable>
            <TableHeader>
              <TableRow>
                <TableHead>Type</TableHead>
                <TableHead className="text-right">Count</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {!stats?.by_type || Object.keys(stats.by_type).length === 0 ? (
                <TableEmpty colSpan={2} message={t('common.noData')} />
              ) : (
                Object.entries(stats.by_type).map(([key, value]: [string, any]) => (
                  <TableRow key={key}>
                    <TableCell>{String(key)}</TableCell>
                    <TableCell className="text-right">{String(value)}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* By Department */}
      <Card>
        <CardHeader>
          <CardTitle>{t('document.byDepartment')}</CardTitle>
          <CardDescription>{t('document.byDepartmentDescription')}</CardDescription>
        </CardHeader>
        <CardContent>
          <Table striped hoverable>
            <TableHeader>
              <TableRow>
                <TableHead>Department</TableHead>
                <TableHead className="text-right">Count</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {!stats?.by_department || Object.keys(stats.by_department).length === 0 ? (
                <TableEmpty colSpan={2} message={t('common.noData')} />
              ) : (
                Object.entries(stats.by_department).map(([key, value]: [string, any]) => (
                  <TableRow key={key}>
                    <TableCell>{String(key)}</TableCell>
                    <TableCell className="text-right">{String(value)}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* By Product */}
      <Card>
        <CardHeader>
          <CardTitle>{t('document.byProduct')}</CardTitle>
          <CardDescription>{t('document.byProductDescription')}</CardDescription>
        </CardHeader>
        <CardContent>
          <Table striped hoverable>
            <TableHeader>
              <TableRow>
                <TableHead>Product</TableHead>
                <TableHead className="text-right">Count</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {!stats?.by_product || Object.keys(stats.by_product).length === 0 ? (
                <TableEmpty colSpan={2} message={t('common.noData')} />
              ) : (
                Object.entries(stats.by_product).map(([key, value]: [string, any]) => (
                  <TableRow key={key}>
                    <TableCell>{String(key)}</TableCell>
                    <TableCell className="text-right">{String(value)}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
});

export default DocumentDashboardPage;
